import styled from '@emotion/styled';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SignupExistingAccount, SignupExistingProvider } from '@/api/repository/types/types';
import SheetDialog from '@/components/common/elements/SheetDialog';
import { navigationService } from '@/common/navigationService';
import { startKakaoLogin, startNaverLogin } from '@/common/socialLoginStart';
import { startAppleSignIn } from '@/native/appleSignIn';
import { FORM_COLOR as C } from '@/pages/Seminar/components/form/formStyles';
import { joinedYearMonthLabel, PROVIDER_LABEL } from '../clinicEmailChange';
import { ChatIcon, ChevronRightIcon, InfoIcon, MailIcon, PersonIcon } from './emailChangeIcons';
import { OutlineButton, PrimaryButton, ResultBox, ResultText, ResultTitle, SubMethodButton } from './hospitalStepStyles';

/**
 * '이미 가입된 치과예요' 안내 — 가입 병원 인증 단계(시안 1-A 이메일 / 1-B 소셜 / 1-C 탈퇴 안내 시트)와
 * 최종 가입이 code 1222 로 막혔을 때(가린 이메일 없이 [가입 이메일 바꾸기]·[로그인]) 같이 쓴다. 운영 가입 화면 형식.
 */

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const AccountCard = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid ${C.line};
    border-radius: 12px;
    background: #fff;
    min-width: 0;

    > span:last-of-type {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    b {
        font-size: 15px;
        color: ${C.text};
        overflow-wrap: anywhere;
    }

    small {
        font-size: 13px;
        color: ${C.muted};
        overflow-wrap: anywhere;
    }
`;

const PROVIDER_COLOR: Record<Exclude<SignupExistingProvider, 'EMAIL'>, { bg: string; fg: string }> = {
    KAKAO: { bg: '#FEE500', fg: '#191600' },
    NAVER: { bg: '#03C75A', fg: '#fff' },
    APPLE: { bg: '#000', fg: '#fff' },
};

const AccountIcon = styled.span<{ provider: SignupExistingProvider }>`
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => (p.provider === 'EMAIL' ? C.primarySoft : PROVIDER_COLOR[p.provider].bg)};
    color: ${(p) => (p.provider === 'EMAIL' ? C.primaryDeep : PROVIDER_COLOR[p.provider].fg)};

    svg {
        display: block;
        width: 19px;
        height: 19px;
    }
`;

/** 소셜 로그인 버튼 — 주 버튼 크기 그대로, 색만 그 소셜(로그인 화면과 같은 색) */
const SocialLoginButton = styled(PrimaryButton)<{ provider: Exclude<SignupExistingProvider, 'EMAIL'> }>`
    background: ${(p) => PROVIDER_COLOR[p.provider].bg};
    color: ${(p) => PROVIDER_COLOR[p.provider].fg};
`;

const OrDivider = styled.p`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 2px 0 -14px;
    font-size: 13px;
    color: ${C.muted};

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: ${C.line};
    }
`;

const OptionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 68px;
    padding: 12px 14px;
    border: 1px solid ${C.border};
    border-radius: 12px;
    background: #fff;
    text-align: left;
    font-family: inherit;
    color: ${C.text};
    cursor: pointer;

    > span {
        flex: 1;
        min-width: 0;
    }

    b {
        display: block;
        font-size: 15px;
        margin-bottom: 3px;
    }

    small {
        display: block;
        font-size: 13px;
        line-height: 1.5;
        color: ${C.muted};
        word-break: keep-all;
    }

    > svg {
        display: block;
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        color: ${C.muted};
    }
`;

const Badge = styled.em`
    display: inline-block;
    margin-left: 6px;
    padding: 2px 6px;
    border-radius: 4px;
    background: ${C.primarySoft};
    color: ${C.primaryDeep};
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    vertical-align: 1px;
`;

/** 탈퇴 안내 시트 — 번호 붙은 순서 */
const Steps = styled.ol`
    list-style: none;
    margin: 0 0 14px;
    padding: 0;
    counter-reset: s;

    li {
        counter-increment: s;
        display: flex;
        gap: 10px;
        padding: 7px 0;
        font-size: 14px;
        line-height: 1.5;
        color: ${C.text};
        word-break: keep-all;
        overflow-wrap: anywhere;
    }

    li::before {
        content: counter(s);
        flex-shrink: 0;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: ${C.primarySoft};
        color: ${C.primaryDeep};
        font-size: 12px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const Note = styled.p`
    margin: 0;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${C.bg};
    font-size: 13px;
    line-height: 1.6;
    color: ${C.sub};
    word-break: keep-all;

    b {
        display: block;
        color: ${C.text};
    }
`;

const SheetButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

interface Props {
    /** 병원 인증에서 찾은 계정. null 이면 최종 가입에서 막힌 경우(가린 이메일 없이 짧은 안내) */
    account: SignupExistingAccount | null;
    /** 가입 중 적은 병원 이름(account.clinicName 이 비었을 때) */
    clinicName: string;
    /** [가입 이메일 바꾸기] */
    onEmailChange: () => void;
}

const ClinicExistingAccountNotice = ({ account, clinicName, onEmailChange }: Props) => {
    const navigate = useNavigate();
    const [sheetOpen, setSheetOpen] = useState<boolean>(false);
    const provider: SignupExistingProvider = account?.provider ?? 'EMAIL';
    const social: Exclude<SignupExistingProvider, 'EMAIL'> | null = provider === 'EMAIL' ? null : provider;
    const name: string = account?.clinicName?.trim() || clinicName;
    const joined: string | null = joinedYearMonthLabel(account?.joinedYearMonth);

    /** 기존 계정으로 로그인 — 이메일이면 로그인 화면, 소셜이면 그 소셜 로그인 */
    const login = async () => {
        if (social === 'KAKAO') {
            if (!startKakaoLogin(null)) navigationService.showToast('카카오 로그인 설정이 필요합니다.', 'error');
            return;
        }
        if (social === 'NAVER') {
            if (!startNaverLogin()) navigationService.showToast('네이버 로그인 설정이 필요합니다.', 'error');
            return;
        }
        if (social === 'APPLE') {
            try {
                const result = await startAppleSignIn('/');
                if (result.status === 'native') navigate('/login/callback/apple', { state: { appleLogin: result.payload } });
            } catch {
                navigationService.showToast('Apple 로그인을 시작할 수 없습니다. 잠시 후 다시 시도해주세요.', 'error');
            }
            return;
        }
        navigate('/login');
    };

    // 최종 가입에서 막힌 경우 — 가린 이메일 없이 두 버튼만
    if (!account) {
        return (
            <ResultBox tone="warn" role="alert" aria-label="이미 가입된 치과" data-existing-clinic="">
                <ResultTitle><InfoIcon />이미 가입된 치과예요</ResultTitle>
                <ResultText>같은 사업자등록번호로 가입한 계정이 있어요. 가입 이메일을 바꾸거나 기존 계정으로 로그인해 주세요.</ResultText>
                <Stack>
                    <PrimaryButton type="button" onClick={onEmailChange}>가입 이메일 바꾸기</PrimaryButton>
                    <OutlineButton type="button" onClick={() => navigate('/login')}>로그인</OutlineButton>
                </Stack>
            </ResultBox>
        );
    }

    const socialLabel: string = social ? PROVIDER_LABEL[social] : '';
    return (
        <>
            <ResultBox tone="warn" role="status" aria-label="이미 가입된 치과" data-existing-clinic="">
                <ResultTitle><InfoIcon />이미 가입된 치과예요</ResultTitle>
                <ResultText>
                    {social
                        ? `${name}은 ${joined ? `${joined}에 ` : ''}${socialLabel} 계정으로 가입했어요.`
                        : `${name}은 ${joined ? `${joined}에 ` : ''}명치사에 가입했어요. 아래 계정으로 로그인해 주세요.`}
                </ResultText>
                <AccountCard>
                    <AccountIcon provider={provider}>{social === 'KAKAO' ? <ChatIcon /> : social ? <PersonIcon /> : <MailIcon />}</AccountIcon>
                    {social ? (
                        <span><b>{socialLabel}로 가입</b>{account.maskedEmail && <small>{account.maskedEmail}</small>}</span>
                    ) : (
                        <span><b>{account.maskedEmail}</b><small>이메일로 가입</small></span>
                    )}
                </AccountCard>
                <Stack>
                    {social ? (
                        <SocialLoginButton type="button" provider={social} onClick={login}>{socialLabel}로 로그인</SocialLoginButton>
                    ) : (
                        <>
                            <PrimaryButton type="button" onClick={login}>이 계정으로 로그인</PrimaryButton>
                            <OutlineButton type="button" onClick={() => navigate('/password-reset')}>비밀번호 찾기</OutlineButton>
                        </>
                    )}
                </Stack>
            </ResultBox>

            <OrDivider>{social ? `이 ${socialLabel} 계정을 쓸 수 없나요?` : '이 이메일을 쓸 수 없나요?'}</OrDivider>
            <Stack>
                <OptionButton type="button" onClick={onEmailChange}>
                    <span>
                        <b>가입 이메일 바꾸기<Badge>추천</Badge></b>
                        <small>
                            {social
                                ? `지금 가입하려던 이메일로 로그인하도록 바꿔요. ${socialLabel} 연결은 끊겨요.`
                                : '사업자등록증으로 확인한 뒤 바꿔요. 공고·지원자 기록은 그대로예요.'}
                        </small>
                    </span>
                    <ChevronRightIcon />
                </OptionButton>
                <OptionButton type="button" onClick={() => setSheetOpen(true)}>
                    <span>
                        <b>탈퇴하고 새로 가입하기</b>
                        <small>기존 계정으로 로그인해서 탈퇴해야 해요.</small>
                    </span>
                    <ChevronRightIcon />
                </OptionButton>
                <SubMethodButton type="button" onClick={() => navigate('/inquiry')}>우리 치과가 아니에요</SubMethodButton>
            </Stack>

            <SheetDialog
                open={sheetOpen}
                title="탈퇴하고 새로 가입하기"
                onClose={() => setSheetOpen(false)}
                footer={(controls) => (
                    <SheetButtons>
                        <PrimaryButton type="button" onClick={() => controls.done(() => { setSheetOpen(false); login(); })}>기존 계정으로 로그인</PrimaryButton>
                        <OutlineButton type="button" onClick={() => controls.done(() => { setSheetOpen(false); onEmailChange(); })}>가입 이메일 바꾸기로 할게요</OutlineButton>
                    </SheetButtons>
                )}
            >
                <Steps>
                    <li>{social ? `기존 ${socialLabel} 계정으로 로그인해요.` : `기존 계정(${account.maskedEmail})으로 로그인해요.`}</li>
                    <li>MY → 치과 정보 [수정] → 계정 보안의 회원 탈퇴를 눌러요.</li>
                    <li>탈퇴하면 바로 다시 가입할 수 있어요.</li>
                </Steps>
                <Note>
                    <b>새 계정으로 옮겨지지 않아요</b>
                    올린 채용 공고, 받은 지원서, 지원자와 나눈 대화가 새 계정에 보이지 않아요. 기록을 그대로 두려면 가입 이메일 바꾸기를 이용해 주세요.
                </Note>
            </SheetDialog>
        </>
    );
};

export default ClinicExistingAccountNotice;
